/*
| Advisor — thin chat drawer for the planning theme. Vanilla TS, no framework.
| Posts each turn to the Advisor `converse` endpoint and renders the grounded
| tool output the backend returns; numbers/lists come from `grounded`, the
| free text only from `reply`. Modeled on the teamhero-ui prototype drawer.
*/

interface DeepLink {
    screen: string;
    params: Record<string, string>;
}

interface PreviewShift {
    date: string;
    time: string;
    workplace?: string;
}

interface PreviewWarning {
    severity: string;
    message: string;
}

interface CommitPayload {
    shifts?: Array<Record<string, unknown>>;
    workplace_id?: number;
    week_start?: string;
    user_id?: number;
    claim_id?: number;
    issue_id?: number;
    resolution?: string;
    shift_id?: number;
    ends_at?: string;
    starts_at?: string;
    split_at?: string;
    operation?: string;
    scope?: Record<string, unknown>;
    value?: Record<string, unknown>;
}

interface PreviewVariant {
    label: string;
    destructive?: boolean;
    shifts?: PreviewShift[];
    warnings?: PreviewWarning[];
    commit: CommitPayload;
}

interface Grounded {
    kind: 'value' | 'list' | 'empty' | 'preview' | string;
    skill?: string;
    number?: number;
    unit?: string;
    label?: string;
    detail?: Record<string, number> | string;
    count?: number;
    rows?: Array<Record<string, unknown>>;
    deep_link?: DeepLink;
    employee?: string;
    workplace?: string;
    shifts?: PreviewShift[];
    warnings?: PreviewWarning[];
    variants?: PreviewVariant[];
    answer?: string;
    intent?: string;
    commit?: CommitPayload;
    summary?: string;
    reason?: string;
}

interface AdvisorConfig {
    endpoint: string;
    persona: 'manager' | 'employee';
    suggestions: string[];
    followups: Record<string, string[]>;
    screens: Record<string, string>;
    screenLabels: Record<string, string>;
    commitEndpoint: string;
    i18n: Record<string, string>;
}

const axios = (): any => (window as any).axios;

export function initAdvisor(): void {
    const island = document.querySelector<HTMLElement>('[data-advisor-config]');
    const panel = document.getElementById('advisor-panel');
    const launcher = document.getElementById('advisor-launch');
    if (!island || !panel || !launcher) {
        return;
    }

    const cfg: AdvisorConfig = JSON.parse(island.textContent || '{}');
    const backdrop = document.querySelector<HTMLElement>('[data-advisor-backdrop]')!;
    const stream = panel.querySelector<HTMLElement>('[data-advisor-stream]')!;
    const form = panel.querySelector<HTMLFormElement>('[data-advisor-form]')!;
    const text = panel.querySelector<HTMLTextAreaElement>('[data-advisor-text]')!;
    const send = panel.querySelector<HTMLButtonElement>('[data-advisor-send]')!;
    const srStatus = document.getElementById('sr-status');

    let conversationId: number | null = null;
    let busy = false;
    let followupTimer: number | undefined;
    let variantSeq = 0;

    renderEmpty();

    const open = (): void => {
        backdrop.hidden = false;
        panel.hidden = false;
        backdrop.classList.add('advisor-backdrop--in');
        panel.classList.add('advisor-panel--in');
        launcher.setAttribute('aria-expanded', 'true');
        text.focus();
    };

    const close = (): void => {
        backdrop.hidden = true;
        panel.hidden = true;
        backdrop.classList.remove('advisor-backdrop--in');
        panel.classList.remove('advisor-panel--in');
        launcher.setAttribute('aria-expanded', 'false');
        launcher.focus();
    };

    const toggle = (): void => (panel.hidden ? open() : close());

    launcher.addEventListener('click', toggle);
    panel.querySelector('[data-advisor-close]')!.addEventListener('click', close);
    backdrop.addEventListener('click', close);

    // Per-issue (and other) "ask the advisor" triggers: open the panel and submit a
    // preset prompt. Delegated so it survives soft-nav #screen swaps.
    document.addEventListener('click', (e: Event) => {
        const trigger = (e.target as HTMLElement).closest<HTMLElement>('[data-advisor-ask]');
        if (!trigger) {
            return;
        }
        e.preventDefault();
        open();
        void ask(trigger.dataset.advisorAsk || '');
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            toggle();
            return;
        }
        if (e.key === 'Escape' && !panel.hidden) {
            close();
        }
    });

    text.addEventListener('input', () => {
        send.disabled = text.value.trim().length === 0;
        window.clearTimeout(followupTimer); // typing cancels the idle follow-up offer
        text.style.height = 'auto';
        text.style.height = `${Math.min(text.scrollHeight, 120)}px`;
    });

    text.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            form.requestSubmit();
        }
    });

    form.addEventListener('submit', (e: Event) => {
        e.preventDefault();
        void ask(text.value.trim());
    });

    async function ask(message: string): Promise<void> {
        if (!message || busy) {
            return;
        }
        busy = true;
        send.disabled = true;
        window.clearTimeout(followupTimer);
        stream.querySelector('.advisor-empty')?.remove();
        stream.querySelector('.advisor-followup')?.remove();
        pushBubble('user', message);
        text.value = '';
        text.style.height = 'auto';
        announce(`${cfg.i18n.you}: ${message}`);

        const typing = pushTyping();
        try {
            const { data } = await axios().post(cfg.endpoint, {
                agent: 'planning',
                message,
                conversation_id: conversationId ?? undefined,
            });
            typing.remove();
            if (data.error) {
                pushBubble('advisor', data.error);
                return;
            }
            conversationId = data.conversation_id ?? conversationId;
            renderAdvisor(data.reply_html || '', data.reply || '', data.grounded || []);
        } catch (err: any) {
            typing.remove();
            pushBubble('advisor', err?.response?.data?.error || cfg.i18n.error);
        } finally {
            busy = false;
        }
    }

    function renderAdvisor(replyHtml: string, reply: string, grounded: Grounded[]): void {
        const msg = pushBubble('advisor', resolveInlineLinks(replyHtml), true);
        grounded.forEach((g) => msg.appendChild(renderGrounded(g)));
        announce(`${cfg.i18n.advisor}: ${reply}`);
        stream.scrollTop = stream.scrollHeight;

        // Follow-up chips: an unsuccessful turn (empty/blocked/disambiguation) offers
        // reason-specific next steps immediately; a successful one offers neutral
        // defaults after a short idle, so the user always has somewhere to go.
        const blocked = grounded.find((g) => ['empty', 'blocked', 'disambiguation'].includes(g.kind));
        if (blocked) {
            showFollowups(cfg.followups[blocked.reason ?? ''] ?? cfg.followups.default ?? []);
        } else {
            followupTimer = window.setTimeout(() => showFollowups(cfg.followups.default ?? []), 5000);
        }
    }

    function showFollowups(items: string[]): void {
        stream.querySelector('.advisor-followup')?.remove();
        if (!items.length) {
            return;
        }
        const block = document.createElement('div');
        block.className = 'advisor-followup';
        block.appendChild(chipRow(items));
        stream.appendChild(block);
        stream.scrollTop = stream.scrollHeight;
    }

    function chipRow(items: string[]): HTMLElement {
        const suggest = document.createElement('div');
        suggest.className = 'advisor-suggest';
        items.forEach((s) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'chip advisor-chip';
            chip.textContent = s;
            chip.addEventListener('click', () => {
                text.value = s;
                send.disabled = false;
                void ask(s);
            });
            suggest.appendChild(chip);
        });
        return suggest;
    }

    // `asHtml` is only ever set for the advisor reply, whose HTML is sanitized
    // server-side (Parsedown safe mode + purifier). User input stays textContent.
    function pushBubble(role: 'user' | 'advisor', body: string, asHtml = false): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = `advisor-msg advisor-msg--${role}`;
        const speaker = role === 'user' ? cfg.i18n.you : cfg.i18n.advisor;
        const dot = role === 'advisor' ? '<span class="brand-dot" aria-hidden="true">●</span> ' : '';
        const bubble = document.createElement('div');
        bubble.className = 'advisor-bubble';
        if (asHtml) {
            bubble.innerHTML = body;
        } else {
            bubble.textContent = body;
        }
        wrap.innerHTML = `<span class="advisor-speaker">${dot}${escapeHtml(speaker)}</span>`;
        wrap.appendChild(bubble);
        stream.appendChild(wrap);
        stream.scrollTop = stream.scrollHeight;
        return wrap;
    }

    function pushTyping(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'advisor-msg advisor-msg--advisor advisor-typing';
        wrap.innerHTML =
            `<span class="advisor-speaker"><span class="brand-dot" aria-hidden="true">●</span> ${escapeHtml(cfg.i18n.advisor)}</span>` +
            '<div class="advisor-bubble"><span class="advisor-typing-dots"><i></i><i></i><i></i></span></div>';
        stream.appendChild(wrap);
        stream.scrollTop = stream.scrollHeight;
        return wrap;
    }

    function renderGrounded(g: Grounded): HTMLElement {
        if (g.kind === 'preview') {
            return renderPreview(g);
        }
        const box = document.createElement('div');
        box.className = 'advisor-grounded';
        if (g.kind === 'value') {
            box.innerHTML =
                `<div class="ag-value">${escapeHtml(String(g.number ?? ''))} <span class="ag-unit">${escapeHtml(g.unit ?? '')}</span></div>` +
                (g.label ? `<div class="ag-note">${escapeHtml(g.label)}</div>` : '');
        } else if (g.kind === 'help' && g.answer) {
            box.innerHTML = `<p class="ag-help">${escapeHtml(g.answer)}</p>`;
        } else if (g.kind === 'list' && g.rows?.length) {
            const ul = document.createElement('ul');
            ul.className = 'advisor-glist';
            g.rows.forEach((r) => ul.appendChild(renderRow(r)));
            box.appendChild(ul);
        }
        const link = deepLink(g.deep_link);
        if (link) {
            box.appendChild(link);
        }
        return box;
    }

    function renderPreview(g: Grounded): HTMLElement {
        if (g.variants?.length) {
            return renderVariants(g);
        }
        const card = document.createElement('div');
        card.className = 'advisor-preview';
        const title = g.summary ?? [g.employee, g.workplace].filter(Boolean).join(' · ');
        card.innerHTML = `<div class="ap-title">${escapeHtml(cfg.i18n.previewTitle)}${title ? ' · ' + escapeHtml(title) : ''}</div>`;

        const ul = document.createElement('ul');
        ul.className = 'advisor-glist ap-shifts';
        (g.shifts ?? []).forEach((s) => {
            const li = document.createElement('li');
            li.innerHTML =
                `<span class="ag-row-main"><span class="ag-row-label">${escapeHtml(formatDate(s.date))}</span>` +
                (s.workplace ? `<span class="ag-row-sub">${escapeHtml(s.workplace)}</span>` : '') +
                `</span><span class="ag-row-val">${escapeHtml(s.time)}</span>`;
            ul.appendChild(li);
        });
        card.appendChild(ul);

        if (g.warnings?.length) {
            const warns = document.createElement('div');
            warns.className = 'ap-warnings';
            g.warnings.forEach((w) => {
                const row = document.createElement('div');
                row.className = 'ap-warn';
                const pillClass = w.severity === 'high' ? 'pill--danger' : 'pill--warn';
                row.innerHTML = `<span class="pill ${pillClass}">!</span><span>${escapeHtml(w.message)}</span>`;
                warns.appendChild(row);
            });
            card.appendChild(warns);
        }

        const actions = document.createElement('div');
        actions.className = 'ap-actions';
        const confirm = document.createElement('button');
        confirm.type = 'button';
        confirm.className = 'btn-cta advisor-confirm';
        confirm.textContent = cfg.i18n.previewConfirm;
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'advisor-cancel-btn';
        cancel.textContent = cfg.i18n.previewCancel;
        const status = document.createElement('div');
        status.className = 'ap-status';
        status.hidden = true;
        confirm.addEventListener('click', () => void commit(g, confirm, cancel, status, card));
        cancel.addEventListener('click', () => card.remove());
        actions.append(confirm, cancel);
        card.append(actions, status);
        return card;
    }

    // A preview offering several fix variants (e.g. issue resolution): each variant
    // is a selectable radio-card; a single confirm commits the chosen one. Mirrors
    // the theme's `.export-fmt` radio-card. Destructive (delete) variants are flagged
    // with a trash icon + irreversible tag, and turn the confirm danger-red when picked.
    function renderVariants(g: Grounded): HTMLElement {
        const card = document.createElement('div');
        card.className = 'advisor-preview';

        const variants = g.variants ?? [];
        const groupName = `ap-variant-${++variantSeq}`;

        const list = document.createElement('div');
        list.className = 'ap-variants';

        const status = document.createElement('div');
        status.className = 'ap-status';
        status.hidden = true;

        const confirm = document.createElement('button');
        confirm.type = 'button';
        confirm.className = 'btn-cta advisor-confirm';
        confirm.textContent = cfg.i18n.previewConfirm;
        confirm.disabled = true;

        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'advisor-cancel-btn';
        cancel.textContent = cfg.i18n.previewCancel;
        cancel.addEventListener('click', () => card.remove());

        let selected = -1;
        const radios: HTMLInputElement[] = [];

        variants.forEach((v, i) => {
            const label = document.createElement('label');
            label.className = 'ap-variant' + (v.destructive ? ' ap-variant--danger' : '');

            const radio = document.createElement('input');
            radio.type = 'radio';
            radio.name = groupName;
            radio.value = String(i);
            radio.className = 'ap-variant__radio';
            radios.push(radio);

            const head = document.createElement('div');
            head.className = 'ap-variant__head';
            head.innerHTML =
                '<span class="ap-variant__dot" aria-hidden="true"></span>' +
                (v.destructive ? TRASH_SVG : '') +
                `<span class="ap-variant__label">${escapeHtml(v.label)}</span>` +
                (v.destructive ? '<span class="pill pill--danger ap-variant__tag">Nevratné</span>' : '');

            const ul = document.createElement('ul');
            ul.className = 'advisor-glist ap-shifts';
            (v.shifts ?? []).forEach((s) => {
                const li = document.createElement('li');
                li.innerHTML =
                    `<span class="ag-row-main"><span class="ag-row-label">${escapeHtml(formatDate(s.date))}</span>` +
                    (s.workplace ? `<span class="ag-row-sub">${escapeHtml(s.workplace)}</span>` : '') +
                    `</span><span class="ag-row-val">${escapeHtml(s.time)}</span>`;
                ul.appendChild(li);
            });

            label.append(radio, head, ul);

            if (v.warnings?.length) {
                const warns = document.createElement('div');
                warns.className = 'ap-warnings';
                v.warnings.forEach((w) => {
                    const row = document.createElement('div');
                    row.className = 'ap-warn';
                    const pillClass = w.severity === 'high' ? 'pill--danger' : 'pill--warn';
                    row.innerHTML = `<span class="pill ${pillClass}">!</span><span>${escapeHtml(w.message)}</span>`;
                    warns.appendChild(row);
                });
                label.appendChild(warns);
            }

            radio.addEventListener('change', () => {
                selected = i;
                confirm.disabled = false;
                confirm.classList.toggle('advisor-confirm--danger', !!v.destructive);
            });

            list.appendChild(label);
        });

        // Pre-select the first non-destructive variant (never default to a delete);
        // if every option is destructive, leave unselected so the user picks explicitly.
        const preselect = variants.findIndex((v) => !v.destructive);
        if (preselect >= 0) {
            radios[preselect].checked = true;
            selected = preselect;
            confirm.disabled = false;
            confirm.classList.toggle('advisor-confirm--danger', !!variants[preselect].destructive);
        }

        confirm.addEventListener('click', () => {
            if (selected < 0) {
                return;
            }
            const v = variants[selected];
            const vg: Grounded = { kind: 'preview', skill: g.skill, commit: v.commit, shifts: v.shifts };
            void commit(vg, confirm, cancel, status, card);
        });

        const actions = document.createElement('div');
        actions.className = 'ap-actions';
        actions.append(confirm, cancel);

        card.append(list, actions, status);
        return card;
    }

    // Confirmed write: the LLM only ever proposes; this is where the human-approved
    // commit hits the generic advisor endpoint, whose skill commit() re-authorizes
    // and re-validates server-side — the echoed payload is not trusted.
    async function commit(
        g: Grounded,
        confirm: HTMLButtonElement,
        cancel: HTMLButtonElement,
        status: HTMLElement,
        card: HTMLElement,
    ): Promise<void> {
        if (!g.skill || !g.commit) {
            return;
        }
        confirm.disabled = true;
        cancel.disabled = true;
        try {
            await axios().post(cfg.commitEndpoint, { skill: g.skill, payload: g.commit });
            status.hidden = false;
            status.textContent = cfg.i18n.previewSuccess;
            announce(cfg.i18n.previewSuccess);
            const date = g.shifts?.[0]?.date;
            const link = deepLink({ screen: 'calendar', params: date ? { date } : {} });
            if (link) {
                card.appendChild(link);
            }
            window.setTimeout(() => window.location.reload(), 1200);
        } catch (err: any) {
            confirm.disabled = false;
            cancel.disabled = false;
            status.hidden = false;
            status.textContent = err?.response?.data?.error || err?.response?.data?.message || cfg.i18n.previewError;
            announce(status.textContent);
        }
    }

    function renderRow(r: Record<string, unknown>): HTMLElement {
        const li = document.createElement('li');
        const date = typeof r.date === 'string' ? formatDate(r.date) : r.date;
        const main = String(r.message ?? r.employee ?? date ?? '');
        const sub = String(r.workplace ?? (r.message ? date : '') ?? '');
        const val = rowValue(r);
        li.innerHTML =
            `<span class="ag-row-main"><span class="ag-row-label">${escapeHtml(main)}</span>` +
            (sub ? `<span class="ag-row-sub">${escapeHtml(sub)}</span>` : '') +
            '</span>' +
            (val ? `<span class="ag-row-val">${escapeHtml(val)}</span>` : '');
        return li;
    }

    function rowValue(r: Record<string, unknown>): string {
        if (r.time) {
            return String(r.time);
        }
        const hourKeys = ['net_hours', 'remaining_hours', 'overtime_hours'];
        for (const k of hourKeys) {
            if (r[k] !== undefined) {
                return `${r[k]} h`;
            }
        }
        return '';
    }

    // Builds a planning URL for a screen key (+ optional date/month/year param).
    // Returns null for unknown screens or malformed params so nothing can route
    // to a bogus address — the single source of truth for every advisor link.
    function screenHref(screen: string, param?: string): string | null {
        const base = cfg.screens[screen];
        if (!base) {
            return null;
        }
        if (param && /^\d{4}(-\d{2}(-\d{2})?)?$/.test(param)) {
            return `${base}/${param}`;
        }
        return base;
    }

    function deepLink(link?: DeepLink): HTMLElement | null {
        if (!link) {
            return null;
        }
        const href = screenHref(link.screen, link.params?.date ?? link.params?.month ?? link.params?.year);
        if (!href) {
            return null;
        }
        const name = cfg.screenLabels?.[link.screen];
        const a = document.createElement('a');
        a.className = 'arrow-link advisor-deeplink';
        a.href = href;
        a.textContent = `→ ${name ? `${cfg.i18n.openPrefix} ${name}` : cfg.i18n.deepLink}`;
        return a;
    }

    // The model is told to reference screens only via [[screen:KEY]] / [[screen:KEY|param=…]]
    // tokens (never raw URLs — its invented ones 404, and <a> is stripped server-side).
    // Resolve those tokens against the screen map into real, highlighted inline links;
    // unknown keys collapse to their label (or vanish), so nothing bogus survives.
    function resolveInlineLinks(html: string): string {
        // Match ANY key (incl. underscores/digits the model occasionally invents,
        // e.g. "week_templates") so a token never survives as raw text. The key is
        // normalised + aliased to a real screen; unknown keys collapse to a label
        // or vanish, never leaking the bracket token.
        return html.replace(/\[\[screen:([a-z0-9_-]+)(?:\|[a-z]+=([\w-]+))?\]\]/gi, (_m, rawKey: string, param?: string) => {
            const screen = resolveScreenKey(rawKey);
            const href = screenHref(screen, param);
            const name = cfg.screenLabels?.[screen] ?? screen;
            if (!href) {
                return cfg.screenLabels?.[screen] ? escapeHtml(name) : '';
            }
            return `<a class="advisor-inline-link" href="${escapeHtml(href)}">${escapeHtml(name)}</a>`;
        });
    }

    // Normalise a model-supplied screen key (lowercase, underscores → hyphens) and
    // map known aliases onto a real screen. Week templates live in the calendar's
    // menu, so template-ish keys resolve there.
    const SCREEN_ALIASES: Record<string, string> = { 'week-templates': 'calendar', templates: 'calendar' };
    function resolveScreenKey(rawKey: string): string {
        const key = rawKey.toLowerCase().replace(/_/g, '-');
        return SCREEN_ALIASES[key] ?? key;
    }

    function renderEmpty(): void {
        const empty = document.createElement('div');
        empty.className = 'advisor-empty';
        empty.innerHTML = `<p class="advisor-welcome">${escapeHtml(cfg.i18n.welcome)}</p>`;
        empty.appendChild(chipRow(sample(cfg.suggestions, 4)));
        stream.appendChild(empty);
    }

    // Fresh random handful from the permission-filtered pool on each empty-state render.
    function sample(pool: string[], count: number): string[] {
        const shuffled = [...pool];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled.slice(0, count);
    }

    function announce(message: string): void {
        if (srStatus) {
            srStatus.textContent = message;
        }
    }
}

// ISO date (YYYY-MM-DD) → Czech human-readable (7.9.2026). Leaves non-ISO values as-is
// so machine `date` fields used for routing stay intact upstream.
function formatDate(value: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return m ? `${+m[3]}.${+m[2]}.${m[1]}` : value;
}

// Lucide-style trash glyph; inherits the danger label colour via currentColor.
const TRASH_SVG =
    '<svg class="ap-variant__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>';

function escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
}
